<template>
  <div class="connection-tools">
    <button type="button" class="tools-open" @click="open = !open; open && load()">WireGuard / IP stack</button>
    <div v-if="open" class="tools-panel" aria-live="polite">
      <p v-if="busy">Loading connection…</p>
      <template v-else-if="settings">
        <label :for="`stack-${license.uuid}`">IP stack</label>
        <select :id="`stack-${license.uuid}`" v-model="selected" :disabled="!settings.can_change_stack || saving">
          <option disabled value="">Provider default</option>
          <option value="ipv4">IPv4 only</option>
          <option value="dual">Dual (IPv4 + IPv6)</option>
        </select>
        <button type="button" :disabled="!settings.can_change_stack || saving || !selected || selected === settings.stack" @click="save">{{ saving ? 'Saving…' : 'Save IP stack' }}</button>
        <p v-if="settings.metered && settings.wireguard_active && selected === 'ipv4'">Saving IPv4 only disconnects WireGuard. Switch back to Dual and download a new configuration to reconnect.</p>
        <p v-if="settings.reason">{{ settings.reason }}</p>
        <p v-if="settings.ipv4">IPv4: {{ settings.ipv4 }}</p>
        <p v-if="settings.ipv6">IPv6: {{ settings.ipv6 }}</p>
        <p v-else-if="settings.stack === 'dual'">IPv6 is not currently reported by this device.</p>
        <button type="button" :disabled="!settings.can_wireguard || downloading" @click="download">{{ downloading ? 'Preparing…' : 'Download WireGuard' }}</button>
        <p v-if="!settings.can_wireguard">{{ settings.wireguard_reason || 'WireGuard requires an eligible plan. Metered access also requires an exclusive phone connection.' }}</p>
        <p v-if="settings.metered">HTTP, SOCKS and WireGuard share your daily download allowance. It resets at 00:00 UTC. Data-capped WireGuard requires Dual stack and an exclusive phone.</p>
      </template>
      <p v-if="error" class="tools-error" role="alert">{{ error }}</p>
      <button v-if="error && !settings" type="button" @click="load">Retry</button>
    </div>
  </div>
</template>
<script setup>
/* global defineProps */
import { ref } from 'vue';
import { useAccountStore } from '@/stores';
const props = defineProps({ license: { type: Object, required: true } });
const open = ref(false), busy = ref(false), saving = ref(false), downloading = ref(false);
const error = ref(''), settings = ref(null), selected = ref('');
const api = process.env.VUE_APP_VUE_API_URL;
async function request(path, body) {
  const token = useAccountStore().user?.accessToken;
  const res = await fetch(api + path, {
    method: body ? 'POST' : 'GET', cache: 'no-store',
    headers: { 'x-access-token': token || '', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) { const data = await res.json().catch(() => ({})); throw new Error(data.message || 'Connection request failed. Please try again.'); }
  return res;
}
async function load() {
  busy.value = true; error.value = '';
  try { settings.value = await (await request('/proxy/connection-tools?license=' + encodeURIComponent(props.license.uuid))).json(); selected.value = settings.value.stack; }
  catch (e) { error.value = e.message; }
  finally { busy.value = false; }
}
async function save() {
  saving.value = true; error.value = '';
  try { const result = await (await request('/proxy/ip-stack', { license: props.license.uuid, stack: selected.value })).json(); Object.assign(settings.value, result); await load(); }
  catch (e) { error.value = e.message; selected.value = settings.value.stack; }
  finally { saving.value = false; }
}
async function download() {
  downloading.value = true; error.value = '';
  let url;
  try {
    const res = await request('/proxy/wireguard', { license: props.license.uuid });
    url = URL.createObjectURL(await res.blob());
    const link = document.createElement('a'); link.href = url; link.download = 'phoneproxy-wireguard.conf';
    document.body.appendChild(link); link.click(); link.remove();
    await load();
  } catch (e) { error.value = e.message; }
  finally { if (url) setTimeout(() => URL.revokeObjectURL(url), 30000); downloading.value = false; }
}
</script>
<style scoped>
.connection-tools { width: 100%; min-width: 0; padding-top: 8px; }
.tools-open { color: #166534; text-decoration: underline; font-weight: 600; }
.tools-panel { margin-top: 8px; padding: 10px; border: 1px solid #cbd5e1; border-radius: 6px; background: #f8fafc; color: #0f172a; font-size: 12px; text-align: left; overflow-wrap: anywhere; }
.tools-panel label { display: block; margin-bottom: 4px; font-weight: 600; }
.tools-panel select { max-width: 100%; width: 100%; padding: 6px; border: 1px solid #94a3b8; background: white; }
.tools-panel button { display: block; margin-top: 8px; padding: 6px 10px; border: 1px solid #15803d; border-radius: 4px; color: #166534; background: white; }
.tools-panel button:disabled, .tools-panel select:disabled { opacity: .55; cursor: not-allowed; }
.tools-panel p { margin-top: 6px; }
.tools-error { color: #b91c1c; }
</style>
